// Sparse interior fill for rooms deep enough to have a dark middle beyond
// the perimeter ring. Grid is aligned to the longest edge and its spacing
// widens so the interior count stays capped. Design doc 2026-08-06 §4.
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const {
  signedPolygonArea,
  minDistanceToPolygonEdges,
} = require('./polygonMath');

function interiorCandidates(floorPolygon, perimeterCans, C) {
  const n = floorPolygon.length;
  let bestLen = 0;
  let theta = 0;
  let perimeterLen = 0;
  for (let i = 0; i < n; i++) {
    const a = floorPolygon[i];
    const b = floorPolygon[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    perimeterLen += len;
    if (len > bestLen) {
      bestLen = len;
      theta = Math.atan2(b.z - a.z, b.x - a.x);
    }
  }

  const area = Math.abs(signedPolygonArea(floorPolygon));
  const interiorArea = Math.max(0, area - perimeterLen * C.INTERIOR_THRESHOLD);
  if (interiorArea <= 0) return [];
  const spacing = Math.max(
    C.INTERIOR_SPACING_BASE,
    Math.sqrt(interiorArea / C.MAX_INTERIOR_CANS)
  );

  // Work in the longest-edge frame so grid rows parallel that wall.
  const cosF = Math.cos(-theta);
  const sinF = Math.sin(-theta);
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of floorPolygon) {
    const rx = p.x * cosF - p.z * sinF;
    const rz = p.x * sinF + p.z * cosF;
    if (rx < minX) minX = rx;
    if (rx > maxX) maxX = rx;
    if (rz < minZ) minZ = rz;
    if (rz > maxZ) maxZ = rz;
  }
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const cosB = Math.cos(theta);
  const sinB = Math.sin(theta);

  const kept = [];
  const all = [...perimeterCans];
  const startX = cx - Math.ceil((cx - minX) / spacing) * spacing;
  const startZ = cz - Math.ceil((cz - minZ) / spacing) * spacing;
  for (let gx = startX; gx <= maxX + 1e-9; gx += spacing) {
    for (let gz = startZ; gz <= maxZ + 1e-9; gz += spacing) {
      const p = { x: gx * cosB - gz * sinB, z: gx * sinB + gz * cosB };
      if (!isPointInsidePolygon(p, floorPolygon)) continue;
      if (
        minDistanceToPolygonEdges(p, floorPolygon) <
        C.INTERIOR_THRESHOLD - 1e-9
      )
        continue;
      if (
        all.some((k) => Math.hypot(k.x - p.x, k.z - p.z) < C.MIN_CAN_SEPARATION)
      )
        continue;
      kept.push(p);
      all.push(p);
    }
  }
  return kept;
}

module.exports = { interiorCandidates };
